import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { api, Transcript, Summary, SourceReference } from '../api/client';

/**
 * Contact Detail View
 * 
 * Displays all transcripts for a contact with summaries,
 * transcript with speaker labels/timestamps, and chat interface.
 * Requirements: 8.4, 8.5, 8.7
 */

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  sources?: SourceReference[];
}

export default function ContactDetail() {
  const { contactId } = useParams<{ contactId: string }>();
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const [summaries, setSummaries] = useState<Map<string, Summary>>(new Map());
  const [selectedTranscript, setSelectedTranscript] = useState<Transcript | null>(null);
  const [expandedSummaries, setExpandedSummaries] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  
  // Chat state
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contactId) {
      loadContactData();
    }
  }, [contactId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  async function loadContactData() {
    if (!contactId) return;
    
    try {
      setLoading(true);
      const transcriptData = await api.getContactTranscripts(contactId);
      setTranscripts(transcriptData);
      
      // Load summaries for each transcript
      const summaryMap = new Map<string, Summary>();
      for (const t of transcriptData) {
        const summary = await api.getSummary(t.transcriptId);
        if (summary) {
          summaryMap.set(t.transcriptId, summary);
        }
      }
      setSummaries(summaryMap);
      
      if (transcriptData.length > 0) {
        setSelectedTranscript(transcriptData[0]);
      }
    } catch (err) {
      console.error('Failed to load contact data:', err);
    } finally {
      setLoading(false);
    }
  }

  async function sendChatMessage() {
    if (!chatInput.trim() || !contactId || chatLoading) return;
    
    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: chatInput.trim(),
    };
    
    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setChatLoading(true);
    
    try {
      const response = await api.chat(contactId, userMessage.content, conversationId || undefined);
      
      if (!conversationId) {
        setConversationId(response.conversationId);
      }
      
      const assistantMessage: ChatMessage = {
        id: response.messageId,
        role: 'assistant',
        content: response.response,
        sources: response.sources,
      };
      
      setChatMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      console.error('Chat error:', err);
      setChatMessages(prev => [...prev, {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
      }]);
    } finally {
      setChatLoading(false);
    }
  }

  function formatTime(ms: number): string {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }

  function formatDate(timestamp: number): string {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  function toggleSummary(transcriptId: string) {
    setExpandedSummaries(prev => {
      const next = new Set(prev);
      if (next.has(transcriptId)) {
        next.delete(transcriptId);
      } else {
        next.add(transcriptId);
      }
      return next;
    });
  }

  function getSentimentClass(sentiment?: string): string {
    switch (sentiment) {
      case 'positive': return 'sentiment-positive';
      case 'negative': return 'sentiment-negative';
      case 'mixed': return 'sentiment-mixed';
      default: return 'sentiment-neutral';
    }
  }

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner" />
        Loading contact data...
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: 24, height: 'calc(100vh - 120px)' }}>
      {/* Left: Transcripts and Summaries */}
      <div style={{ overflow: 'auto' }}>
        <h2 style={{ marginBottom: 16 }}>
          Contact {contactId?.substring(0, 8)}
        </h2>
        
        {transcripts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📝</div>
            <p>No transcripts found for this contact</p>
          </div>
        ) : (
          <div>
            {/* Transcript List with Summaries */}
            {transcripts.map(transcript => {
              const summary = summaries.get(transcript.transcriptId);
              const isExpanded = expandedSummaries.has(transcript.transcriptId);
              const isSelected = selectedTranscript?.transcriptId === transcript.transcriptId;
              
              return (
                <div key={transcript.transcriptId} className="expandable">
                  <div 
                    className="expandable-header"
                    onClick={() => toggleSummary(transcript.transcriptId)}
                    style={{ background: isSelected ? 'var(--border)' : undefined }}
                  >
                    <div>
                      <span style={{ fontWeight: 600 }}>
                        {formatDate(transcript.callDate)}
                      </span>
                      {summary && (
                        <span className={`sentiment ${getSentimentClass(summary.sentiment)}`} style={{ marginLeft: 8 }}>
                          {summary.sentiment}
                        </span>
                      )}
                      <span style={{ color: 'var(--text-secondary)', marginLeft: 8, fontSize: 13 }}>
                        {formatTime(transcript.durationMs)}
                      </span>
                    </div>
                    <span className={`expandable-arrow ${isExpanded ? 'open' : ''}`}>▼</span>
                  </div>
                  
                  {isExpanded && (
                    <div className="expandable-content">
                      {summary ? (
                        <div>
                          <p style={{ marginBottom: 12 }}>{summary.quickSummary}</p>
                          
                          {summary.keyPoints.length > 0 && (
                            <div style={{ marginBottom: 12 }}>
                              <strong>Key Points:</strong>
                              <ul style={{ marginLeft: 20, marginTop: 4 }}>
                                {summary.keyPoints.map((point, i) => (
                                  <li key={i}>{point}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {summary.actionItems.length > 0 && (
                            <div style={{ marginBottom: 12 }}>
                              <strong>Action Items:</strong>
                              <ul style={{ marginLeft: 20, marginTop: 4 }}>
                                {summary.actionItems.map((item, i) => (
                                  <li key={i}>
                                    {item.task}
                                    {item.owner && <span style={{ color: 'var(--text-secondary)' }}> ({item.owner})</span>}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {summary.followUpRequired && (
                            <div style={{ color: 'var(--warning)', marginBottom: 12 }}>
                              ⚠️ Follow-up required: {summary.followUpReason}
                            </div>
                          )}
                        </div>
                      ) : (
                        <p style={{ color: 'var(--text-secondary)' }}>No summary available</p>
                      )}
                      
                      <button 
                        className="btn btn-secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedTranscript(transcript);
                        }}
                        style={{ marginTop: 12 }}
                      >
                        View Full Transcript
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
            
            {/* Selected Transcript View */}
            {selectedTranscript && (
              <div className="transcript-view" style={{ marginTop: 24 }}>
                <h3 style={{ marginBottom: 16 }}>
                  Transcript - {formatDate(selectedTranscript.callDate)}
                </h3>
                
                {selectedTranscript.utterances.map((utterance, i) => (
                  <div key={i} className="utterance">
                    <div className="utterance-header">
                      <span className="utterance-speaker">
                        Speaker {utterance.speaker}
                      </span>
                      <span className="utterance-time">
                        {formatTime(utterance.start)}
                      </span>
                    </div>
                    <p className="utterance-text">{utterance.text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Right: Chat Interface */}
      <div className="card chat-container">
        <div className="card-header">
          <h3 className="card-title">Ask about this contact</h3>
        </div>
        
        <div className="chat-messages">
          {chatMessages.length === 0 && (
            <div className="empty-state" style={{ padding: 20 }}>
              <p>Ask questions about this contact's call history</p>
            </div>
          )}
          
          {chatMessages.map(msg => (
            <div 
              key={msg.id} 
              className={`chat-message chat-message-${msg.role}`}
            >
              <p>{msg.content}</p>
              
              {msg.sources && msg.sources.length > 0 && (
                <div className="chat-sources">
                  <strong>Sources:</strong>
                  {msg.sources.map((source, i) => (
                    <div key={i} style={{ marginTop: 4 }}>
                      📞 {source.callDate}: "{source.snippet}"
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          
          {chatLoading && (
            <div className="chat-message chat-message-assistant">
              <div className="spinner" style={{ width: 16, height: 16 }} />
            </div>
          )}
          
          <div ref={chatEndRef} />
        </div>
        
        <div className="chat-input-container">
          <input
            type="text"
            className="input"
            placeholder="Ask a question..."
            value={chatInput}
            onChange={e => setChatInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && sendChatMessage()}
            disabled={chatLoading}
          />
          <button 
            className="btn btn-primary"
            onClick={sendChatMessage}
            disabled={chatLoading || !chatInput.trim()}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
